import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MicrositeContent } from '@/types/microsite';
import { micrositeService } from '@/services/microsite.service';
import { MicrositeNav } from '@/components/microsite/MicrositeNav';

export default function MicrositePlanPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<MicrositeContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shareId) {
      navigate('/p/error', { replace: true });
      return;
    }
    micrositeService
      .getContent(shareId)
      .then(setData)
      .catch(() => navigate('/p/error', { replace: true }))
      .finally(() => setLoading(false));
  }, [shareId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#bd9734] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const firstName = data.player.name.trim().split(/\s+/)[0];

  return (
    <div className="max-w-screen-sm mx-auto p-4 pb-24 space-y-6">
      <header className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Your Plan</h1>
        <p className="text-sm text-gray-600">Personalized for {firstName}</p>
      </header>

      <section className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-6 text-white">
        <h2 className="text-xl font-semibold mb-2">Our Philosophy</h2>
        <p className="text-gray-300 text-sm mb-2">
          The fastest way to get better is by keeping your plan simple, focusing on a few key
          priorities, and practicing them with purpose.
        </p>
        <p className="text-gray-300 text-sm">
          Better golf starts with the body. Address movement limitations to unlock efficient
          sequencing and a repeatable swing.
        </p>
      </section>

      <section className="space-y-4">
        {/* Step 1: TPI Prescriptive Program */}
        <div className="space-y-3">
          <div className="flex items-center mb-1">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-50 rounded-xl mr-3">
              <span className="text-base font-bold text-yellow-600">1</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Follow your TPI Prescriptive Program
            </h3>
            <span className="ml-auto text-xs text-gray-500">2–3x/week</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
            <div className="relative" style={{ paddingTop: '56.25%' }}>
              <img
                src="/images/tpi.jpg"
                alt="TPI program"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <h4 className="text-white text-lg font-semibold drop-shadow">
                  TPI Prescriptive Program
                </h4>
                <p className="text-white/85 text-sm drop-shadow">
                  Mobility, strength, and prep work
                </p>
              </div>
            </div>
          </div>
          <ul className="list-disc pl-5 text-sm text-gray-700">
            <li>Open weak areas</li>
            <li>Improve mobility and strength</li>
            <li>Use before practice/rounds</li>
          </ul>
          <div className="mt-4 text-center">
            <p className="text-gray-700 text-sm mb-3">
              Download the TPI app to access your personalized exercises
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://apps.apple.com/us/app/tpi-pro/id1191628131"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/app-store.png"
                  alt="Download on the App Store"
                  className="h-12 w-auto hover:opacity-80 transition-opacity"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.mytpi.mytpi&hl=en_US&pli=1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/play.png"
                  alt="Get it on Google Play"
                  className="h-12 w-auto hover:opacity-80 transition-opacity"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Step 2: Practice at Tee Box */}
        <div className="space-y-3">
          <div className="flex items-center mb-1">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-50 rounded-xl mr-3">
              <span className="text-base font-bold text-yellow-600">2</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Practice at Tee Box</h3>
            <span className="ml-auto text-xs text-gray-500">weekly</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
            <div className="relative" style={{ paddingTop: '56.25%' }}>
              <img
                src="/images/teebox-exterior.jpg"
                alt="Tee Box facility exterior"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <h4 className="text-white text-lg font-semibold drop-shadow">
                  Practice at Tee Box
                </h4>
                <p className="text-white/85 text-sm drop-shadow">Guided, tech-enabled sessions</p>
              </div>
            </div>
          </div>
          <ul className="list-disc pl-5 text-sm text-gray-700">
            <li>Focus on your 3D System goals</li>
            <li>Work assigned drills</li>
            <li>Targeted practice with Trackman</li>
          </ul>
          <div className="mt-1 p-3 bg-yellow-50 rounded-xl">
            <p className="text-yellow-700 text-[11px] font-medium uppercase tracking-wide mb-1">
              Focused Practice
            </p>
            <p className="text-yellow-800 text-sm">
              Sharpen specific areas like wedges, tee shots, or putting. Use Trackman rounds with
              intent; your Box Pro can guide focus with technology.
            </p>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-700 text-[11px] font-medium uppercase tracking-wide mb-2">
              Download Tee Box App
            </p>
            <p className="text-gray-800 text-sm mb-3">
              Book sessions, track progress, and access your training programs
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://apps.apple.com/us/app/the-tee-box/id6630387855"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/app-store.png"
                  alt="Download on the App Store"
                  className="h-12 w-auto hover:opacity-80 transition-opacity"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.playbypoint.teebox&hl=en_US"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/play.png"
                  alt="Get it on Google Play"
                  className="h-12 w-auto hover:opacity-80 transition-opacity"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Step 3: Consistency is the Key */}
        <div className="space-y-3">
          <div className="flex items-center mb-1">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-50 rounded-xl mr-3">
              <span className="text-base font-bold text-yellow-600">3</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Consistency is the Key</h3>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
            <div className="relative" style={{ paddingTop: '56.25%' }}>
              <img
                src="/images/consistency.webp"
                alt="Consistency"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <h4 className="text-white text-lg font-semibold drop-shadow">Consistency</h4>
                <p className="text-white/85 text-sm drop-shadow">Quality reps over time</p>
              </div>
            </div>
          </div>
          <ul className="list-disc pl-5 text-sm text-gray-700">
            <li>Quality reps over time</li>
            <li>Trust the process</li>
            <li>Leverage your Box Pro</li>
          </ul>
        </div>
      </section>

      <MicrositeNav shareId={shareId!} />
    </div>
  );
}
